import { ReactNode } from 'react';
import { components } from '@api/club/generated';
import { components as accountComponents } from '@api/play-account/generated';
import { components as userComponents } from '@api/play-user/generated';

export type ParticipantStatus = components['schemas']['ParticipantStatus'];

export type TournamentStatus = components['schemas']['TournamentStatus'];

export type TournamentEntrance = 'Open' | 'Rank' | 'Invite';
export type TournamentMode = components['schemas']['TournamentMode'];
export type ClockControlType = components['schemas']['ClockControlType'];
export const ClockControlTypes: ClockControlType[] = ['Blitz', 'Rapid', 'Classic', 'Bullet'];

export type ClockControlSettings = components['schemas']['ClockControlSettings'];

export type TournamentHighlight = components['schemas']['TournamentHighlight'];

export type TournamentAdditionalScore =
  | 'Berger'
  | 'PartBerger'
  | 'Match'
  | 'Black'
  | 'ScoreSum'
  | 'Buchholz'
  | 'TruncatedBuchholz'
  | 'IndividualBuchholzSum'
  | 'IndividualPlaceSum';
export type TournamentAdditionalScoreDto = components['schemas']['TournamentAdditionalScore'];

export type TournamentSystem = 'RoundRobin' | 'Swiss' | 'Knockout' | 'OneOnOne' | 'Seance';

export type GameSide = 'Black' | 'White' | 'Both' | 'Unknown';

export type GameParticipantsResult =
  | 'Win'
  | 'Loss'
  | 'Draw'
  | 'InProgress'
  | 'UnPlayed'
  | 'Skipped'
  | 'Mixed'
  | 'ByeIn'
  | 'Banned'
  | 'NoShow';

export type KnockoutDraftOrder = components['schemas']['KnockoutDraftOrder'];

export type ResultsTableAdditionalScore = {
  scoreType?: TournamentAdditionalScore;
  type?: TournamentAdditionalScore;
  score: number | null;
};

/*
  - NoGamePossible — Игра невозмиожна (например, играс с самим собой в таблице круговика)
  - NotDrafted — Игры нет, не было жеребьёвки
  - NotStarted — Игра пока не началась
  - InProgress — Игра идёт
  - Win — победа
  - Loss — Поражение
  - Draw — Ничья
  - ForfeitWin — Техническая победа (+): соперник не ходил (игра есть)
  - ForfeitLoss - Техническое поражение (-): игрок не ходил (игра есть)
  - UnpairedBye — Bye: игроку не нашлось пары в жеребьёвке, автоматическая победа
  - ZeroPointBye — Участник удалён (игры нет)
  - BanLoss — Поражение: игрок забанен, оригинальный результат переписан (игра есть)
  - BanForfeitLoss — Техническое поражение (-): игрок забанен, оригинальный результат переписан (игра есть)
  - BanBye — У игрока бан (игры нет)
  */
export type CellResult = components['schemas']['CellResultType'];

export type ResultsGame = {
  gameId?: string | null;
  roundNo?: number;
  opponentSeedNumber?: number;
  score?: number | null;
  resultType?: GameParticipantsResult;
  side?: GameSide;
  cellResult?: CellResult;
};
export type ResultsTableParticipant = {
  partyId: string | null;
  no: number;
  seedNumber?: number;
  title: string;
  elo?: number;
  games: ResultsGame[];
  games2?: ResultsGame[];
  additionalScores?: ResultsTableAdditionalScore[];
  totalScore?: number | null;
  place?: string;
  subRows?: ResultsTableParticipant[] | null;
  isAbsent?: boolean;
};
export type ResultsTableRound = {
  num: number;
  members: ResultsTableParticipant[];
  gamesCount: number;
  additionalScoreTypes?: TournamentAdditionalScore[];
};
export type ResultsTableData = {
  rounds: ResultsTableRound[];
};

export type GameMovementRow = ParticipantGameMovementRow | TeamGameMovementRow;

export type ParticipantGameMovementRow = components['schemas']['RoundResultsRow'];

export type TeamGameMovementRow = components['schemas']['RoundResultsTeamRow'];

export type GameMovementPlayer = components['schemas']['RoundResultsPlayer'];

export type GameMovementTeam = components['schemas']['RoundResultsTeam'];

export type ParticipantDto = components['schemas']['ParticipantDto'];

export type MemberTournamentParticipantDto =
  components['schemas']['MemberTournamentParticipantDto'];

export type TournamentTeam = {
  teamId: string;
  name: string;
  tournamentId: string;
  clubId: string;
};

export type TournamentRound = {
  roundId: string;
  roundNumber: number;
  cycleNumber: number | null;
  status: 'New' | 'Drafted' | 'InProgress' | 'Finished' | 'Deleted';
  startTimestampMs?: number | null;
};

export type Contact = {
  name: string;
  label: string;
  uri: string;
};
export type ClubModeratorDto = {
  moderatorId: string;
  email: string;
};
export type ClubAdminDto = {
  fullName: string;
  phoneNumber: string;
  email: string;
};
export type AdminManagerDto = components['schemas']['AdminManagerDto'];

export type NumberInTeamMode = components['schemas']['NumberInTeamMode'];
export type PrimaryScoringMethod = components['schemas']['PrimaryScoringMethod'];

export type Tournament = {
  clubId: string;
  tournamentId: string;
  status: TournamentStatus;
  title: string;
  logoUrl?: string | null;
  startTimestampMs?: number | null;
  registrationTillTimestampMs?: number | null;
  highlight: TournamentHighlight;
  entrance: TournamentEntrance;
  registrationClosedBeforeStartMinutes: number | null;
  participantPreModerationEnabled: boolean;
  minEntranceElo?: number | null;
  contacts: Contact[];
  pausedTillTimestampMs?: number | null;
  rulesUrl?: string | null;
  rcfRankCalculated: boolean;
  mode: TournamentMode;
  clockControl: ClockControlType;
  clockControlSettingsPlayer1: ClockControlSettings;
  clockControlSettingsPlayer2: ClockControlSettings;
  additionalScoreOrder: TournamentAdditionalScore[];
  system: TournamentSystem;
  teams?: TournamentTeam[];
  teamModeSettings?: {
    size: number;
    numberInTeamMode: NumberInTeamMode;
    primaryScoringMethod?: PrimaryScoringMethod;
  };
  privateTeamModeSettings?: {
    playersToConsiderForScore?: number | null;
    tieBreakers?: TournamentAdditionalScore[] | null;
  };
  rounds: TournamentRound[];
  participantsCount: number;
  participantsCountMax: number | null;
  teamsCount: number | null;
  teamsCountMax: number | null;
  delayBetweenRoundsSeconds: number;
  roundRobinSettings?: RoundRobinSettings;
  oneOnOneSettings?: OneOnOneSettings;
  knockOutSettings?: KnockOutSettings;
  swissSettings?: SwissSettings;
  seanceSettings?: SeanceSettingsDto;
  participationStatus?: ParticipantStatus;
  automatedSystemChangeEnabled: boolean | null;
  sponsors?: Sponsor[] | null;
  initialFen: string | null;
  isBlind: boolean;
  participationResultType?: components['schemas']['ParticipantResultType'];
  participationResultPlace?: string | null;
  participationResultRating?: number | null;
  participationResultRatingChange?: number | null;
  orgSettings: components['schemas']['TournamentOrgSettings'];
  paymentSettings?: components['schemas']['TournamentPaymentSettings'] | null;
  joinEligibility: components['schemas']['ParticipantJoinEligibility'];
  customRoundDelays: components['schemas']['RoundDelay'][] | null;
  entranceTicketSets?: string[][] | null | undefined;
  removePlayerSetting: RemovePlayerSetting;
  videoSettings?: TournamentVideoSettings;
  videoOpenTimestampMs?: number | null;
  videoRoomsCount?: number;
};

export type TournamentVideoSettings = components['schemas']['TournamentVideoSettings'];

export type RoundRobinSettings = components['schemas']['RoundRobinSettings'];
export type SwissSettings = components['schemas']['SwissSettings'];
export type KnockOutSettings = {
  gamesPerRound: number;
  gamesPerFinalRound: number;
  draftOrder?: KnockoutDraftOrder;
  tieSettings: {
    additionalGamesCount: number;
    additionalGamesFinalCount: number;
    clockControl: ClockControlType;
    clockControlSettingsPlayer1: ClockControlSettings;
    clockControlSettingsPlayer2: ClockControlSettings;
  };
  armageddonSettings: {
    clockControl: ClockControlType;
    clockControlSettingsPlayer1: ClockControlSettings;
    clockControlSettingsPlayer2: ClockControlSettings;
  };
  maxPartiesCount: number | null;
};
export type OneOnOneSettings = components['schemas']['OneOnOneSettings'];
export type OneOnOneColorAllocation = components['schemas']['OneOnOneColorAllocation'];
export type SeanceSettingsDto = components['schemas']['SeanceSettingsDto'];

export type ClubDto = components['schemas']['ClubDto'];
export type MemberClubDto = components['schemas']['MemberClubDto'];

export type ClubGroup = {
  id: number;
  title: string;
  rcfId?: string;
  participantIds: number[];
};

export type MemberDto = components['schemas']['MemberDto'];

export type MemberStatus = components['schemas']['MemberStatus'];

export type ClubEntrance = components['schemas']['ClubEntrance'];

export type UserEloDto = {
  blitz: EloDto;
  rapid: EloDto;
  classic: EloDto;
};

export type EloDto = {
  clockControl: ClockControlType;
  gamesCount: number;
  rating: number;
  lastChange: number;
};

export type UpdateTournamentRequest = {
  title: string;
  startTimestampMs: number;
  highlight: TournamentHighlight;
  entrance: TournamentEntrance;
  participantPreModerationEnabled: boolean;
  minEntranceElo?: number | null;
  contacts: Contact[];
  rcfRankCalculated: boolean;
  mode: TournamentMode;
  clockControl: ClockControlType;
  clockControlSettingsPlayer1: ClockControlSettings;
  clockControlSettingsPlayer2: ClockControlSettings;
  additionalScoreOrder: TournamentAdditionalScoreDto[];
  system: TournamentSystem;
  knockOutSettings?: KnockOutSettings;
  swissSettings?: SwissSettings;
  roundRobinSettings?: RoundRobinSettings;
  seanceSettings?: SeanceSettingsDto;
  teamModeSettings?: {
    size: number;
    numberInTeamMode: NumberInTeamMode;
    primaryScoringMethod?: PrimaryScoringMethod;
  };
  privateTeamModeSettings?: {
    playersToConsiderForScore?: number | null;
    tieBreakers?: TournamentAdditionalScore[] | null;
  };
  oneOnOneSettings?: OneOnOneSettings;
  seedOnlyOnlineParticipants?: boolean;
  initialFen?: string | null;
  isBlind?: boolean;
  orgSettings?: {
    organizer: string | null;
    chiefArbiter: string | null;
    arbiters: string[];
  };
  customRoundDelays: components['schemas']['RoundDelay'][] | null;
  removePlayerSetting: RemovePlayerSetting;
  videoSettings?: TournamentVideoSettings;
};

// KnockoutResults

export type KnockoutResultsPlayer = {
  userId: string;
  lastName: string;
  firstName: string;
  games: ResultsGame[];
  total: {
    score: string;
    resultType: GameParticipantsResult;
  };
  subRows?: KnockoutResultsPlayer[] | null;
};

export type KnockoutResultsMatch = {
  knockoutRoundType?: KnockoutRoundType;
  player1?: KnockoutResultsPlayer;
  player2?: KnockoutResultsPlayer;
};

export type KnockoutRoundType = 'WinnerBranch' | 'LoosersBranch' | 'Final' | 'ThirdPlace';

export type KnockoutResultsRound = {
  roundId: string;
  roundName: string;
  roundType: KnockoutRoundType;
  matches: KnockoutResultsMatch[];
};

export type KnockoutResults = {
  rounds: KnockoutResultsRound[];
};

export type MyClubStatType = {
  clockControlType: ClockControlType;
  clubRank: number | null;
  gamesCount: number;
  winCount: number;
  drawCount: number;
};

export type MyClubStatsType = {
  blitz: MyClubStatType;
  rapid: MyClubStatType;
  classic: MyClubStatType;
  totalGamesCount: number;
  totalTournamentsCount: number;
  totalWinCount: number;
  winsAsWhiteCount: number;
  winsAsBlackCount: number;
  recentGames: {
    tournamentId: string;
    gameId: string;
    fen?: string | null;
    whitePlayer: { userId: string; name: string; score: number };
    blackPlayer: { userId: string; name: string; score: number };
    mySide: 'Black' | 'White';
    result:
      | 'InProgress'
      | 'NotStarted'
      | 'NotPlayed'
      | 'Unknown'
      | 'Draw'
      | 'WhiteWin'
      | 'BlackWin';
  }[];
};

export type TopPlayer = {
  gamesCount: number;
  winsCount: number;
  clubRank: number;
  clubRating: number;
  member: components['schemas']['TopPlayerMemberDto'];
};

export type NewsPost = {
  id: string;
  title: string;
  publishTimestampMs: number;
  text: string;
  promoted: boolean;
};

export type Winner = {
  tournamentId: string;
  tournamentTitle: string;
  winnerTitles: string[];
};

export type Sponsor = components['schemas']['SponsorDto'];

export type PlayerStats = {
  firstName: string;
  lastName: string;
  avatarSrc?: string | null;
  totalGamesCount: number;
  totalTournamentsCount: number;
  totalWinCount: number;
  totalRate: number;
  stamina: number;
  tactics: number;
  rate: number;
  experience: number;
  efficiency: number;
};

export type PlayerEloStat = {
  clockControl: string;
  gamesCount: number;
  rating: number;
  lastChange: number;
};

export type PlayerEloStats = {
  blitz: PlayerEloStat;
  rapid: PlayerEloStat;
  classic: PlayerEloStat;
};

export type ChartPoint = {
  x: number;
  y: number;
  label?: ReactNode | null;
};

export type EloHistoryPeriodType = accountComponents['schemas']['EloHistoryPeriodType'];

export type RcfAttributes = components['schemas']['SetUsersRcfIdRequestItem'] & {
  rcfIdApproved: boolean;
};
export type Gender = components['schemas']['Gender'];

export type Achievement = userComponents['schemas']['UserAchievementDto'];

export type RemovePlayerSetting = components['schemas']['RemovePlayerSetting'];
